import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import ProductsSearch from '../product/components/ProductsSearch';
import '../business/Business.css';
import './CategoryProductsPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import CreateFooter from '../business/components/CreateFooter';

export default function CategoryProductsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId } = useParams();
  const { loading, requestWithMeta } = useHttp();

  const [category, setCategory] = React.useState(null);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [pageLoading, setPageLoading] = React.useState(true);
  const [submitError, setSubmitError] = React.useState(null);

  const skeletonItems = React.useMemo(() => Array.from({ length: 4 }, (_, i) => i), []);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      setPageLoading(true);
      setSubmitError(null);

      try {
        const stateCategory = location?.state?.category ?? null;
        const stateId = stateCategory?.id ?? stateCategory?._id ?? null;

        let categoryData = null;
        if (stateCategory && stateId && String(stateId) === String(categoryId)) {
          categoryData = stateCategory;
        } else {
          const catRes = await requestWithMeta(
            `/operator/categories/${encodeURIComponent(categoryId)}`,
            'GET'
          );
          if (cancelled) return;
          if (!catRes?.ok) {
            setSubmitError(catRes?.data?.error || catRes?.data?.message || 'Не удалось загрузить категорию');
            return;
          }
          categoryData = catRes?.data?.category ?? null;
        }

        const productIds = Array.isArray(categoryData?.productIds) ? categoryData.productIds : [];
        setCategory(categoryData);
        setSelectedIds(productIds);
      } catch (_) {
        if (!cancelled) setSubmitError('Ошибка загрузки');
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [categoryId, location?.state, requestWithMeta]);

  const selectedSet = React.useMemo(
    () => new Set(selectedIds.map(String)),
    [selectedIds]
  );

  const toggleProduct = React.useCallback((productId) => {
    const key = String(productId);
    setSelectedIds((prev) => {
      const exists = prev.some((id) => String(id) === key);
      if (exists) return prev.filter((id) => String(id) !== key);
      return [...prev, productId];
    });
  }, []);

  const selectedCount = selectedIds.length;
  const isSubmitDisabled = loading || pageLoading || !category;

  async function handleSubmit() {
    setSubmitError(null);

    const productIds = selectedIds
      .map((id) => Number.parseInt(String(id), 10))
      .filter((id) => Number.isFinite(id));

    const res = await requestWithMeta(
      `/operator/categories/${encodeURIComponent(categoryId)}/products`,
      'PUT',
      { productIds }
    );

    if (!res?.ok) {
      setSubmitError(res?.data?.error || res?.data?.message || 'Не удалось сохранить товары');
      return;
    }

    navigate('/categories', { replace: true });
  }

  const categoryName = category?.name ?? 'Категория';

  return (
    <div className="shop-create-root category-products-page-root">
      <div className="shop-create-mobile">
        <CreateBusinessHeader
          title={categoryName}
          subtitle="Выберите товары для категории"
          onBack={() => navigate('/categories')}
          backClassName="category-products-page-backButton"
        />

        <main className="shop-create-content">
          {submitError ? (
            <section className="shop-create-section shop-create-sectionError">
              <div className="shop-create-label">Ошибка</div>
              <div className="shop-create-help shop-create-helpTight">{submitError}</div>
            </section>
          ) : null}

          <section className="shop-create-section">
            <ProductsSearch
              emptyText="Товары не найдены."
              toolbar={({ total, loading: productsLoading }) => (
                <div className="category-products-page-listHeader">
                  <div className="shop-create-label category-products-page-listTitle">Товары</div>
                  <div className="shop-create-help shop-create-helpTight">
                    {pageLoading || productsLoading
                      ? 'Загрузка…'
                      : selectedCount > 0
                        ? `Выбрано: ${selectedCount}`
                        : `${total} товаров`}
                  </div>
                </div>
              )}
            >
              {({ products, loading: productsLoading }) => (
                <div className="category-products-page-list">
                  {productsLoading && products.length === 0
                    ? skeletonItems.map((item) => (
                        <div key={item} className="category-products-page-item category-products-page-item--skeleton">
                          <div className="shop-create-skeleton category-products-page-skeletonImage" />
                          <div className="shop-create-skeleton shop-create-skeleton--text category-products-page-skeletonName" />
                          <div className="shop-create-skeleton category-products-page-skeletonBadge" />
                        </div>
                      ))
                    : products.map((product) => {
                        const productId = product?._id ?? product?.id;
                        const isSelected = selectedSet.has(String(productId));

                        return (
                          <button
                            key={productId}
                            type="button"
                            className={`category-products-page-item${isSelected ? ' category-products-page-item--selected' : ''}`}
                            onClick={() => toggleProduct(productId)}
                            aria-pressed={isSelected}
                            aria-label={
                              isSelected
                                ? `${product?.name || 'Товар'}. Нажмите, чтобы убрать`
                                : `${product?.name || 'Товар'}. Нажмите, чтобы выбрать`
                            }
                          >
                            <img
                              className="category-products-page-image"
                              src={product?.imageUrl || ''}
                              alt=""
                              loading="lazy"
                            />

                            <div className="category-products-page-main">
                              <div className="category-products-page-name">{product?.name || 'Без названия'}</div>
                            </div>

                            <div
                              className={`category-products-page-checkBadge${isSelected ? ' category-products-page-checkBadge--filled' : ''}`}
                              aria-hidden="true"
                            >
                              {isSelected ? '✓' : ''}
                            </div>
                          </button>
                        );
                      })}
                </div>
              )}
            </ProductsSearch>
          </section>
        </main>

        <CreateFooter
          disabled={isSubmitDisabled}
          label={loading ? 'Сохранение…' : 'Сохранить'}
          onCreate={handleSubmit}
        />
      </div>
    </div>
  );
}
